import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Building2, CheckCircle, Clock, Mail } from "lucide-react";
import type { CompanyAccount } from "@/hooks/useCompanyAccount";
import { DemoHint } from "@/components/demo/DemoHint";

interface Props {
  company: CompanyAccount;
}

export function CompanyOverviewTab({ company }: Props) {
  const verificationBadge = () => {
    const status = company.verification_status;
    if (status === "verified") return <Badge className="bg-success text-success-foreground"><CheckCircle className="h-3 w-3 mr-1" /> Verified</Badge>;
    if (status === "pending") return <Badge variant="secondary" className="bg-warning-subtle text-warning-foreground"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
    return <Badge variant="secondary">Unverified</Badge>;
  };

  const mailtoUrl = `mailto:support@lanerunner.com?subject=${encodeURIComponent(`Company Info Change Request — ${company.name}`)}&body=${encodeURIComponent(`Company: ${company.name}\nMC#: ${company.mc_number || 'N/A'}\n\nPlease describe the change you need:\n`)}`;

  return (
    <div className="space-y-6">
      <Card className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">

          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DemoHint hintKey="section.companyOverview" side="right" showIndicator>
              <h3 className="font-semibold text-foreground">{company.name}</h3>
              </DemoHint>
              {company.legal_name && company.legal_name !== company.name && (
                <p className="text-sm text-muted-foreground">{company.legal_name}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {verificationBadge()}
            <Button variant="outline" size="sm" asChild>
              <a href={mailtoUrl}>
                <Mail className="h-3.5 w-3.5 mr-1.5" /> Request Change
              </a>
            </Button>
          </div>
        </div>

        {/* Identity (read-only) — hide MC/DOT for shippers (carrier-only fields) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 pb-6 border-b border-border">
          {company.company_type !== "shipper" && (
            <>
              <div>
                <Label className="text-muted-foreground text-xs">MC Number</Label>
                <p className="font-medium text-sm text-foreground">{company.mc_number || "—"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">DOT Number</Label>
                <p className="font-medium text-sm text-foreground">{company.dot_number || "—"}</p>
              </div>
            </>
          )}
          <div>
            <Label className="text-muted-foreground text-xs">Type</Label>
            <p className="font-medium text-sm text-foreground capitalize">{company.company_type}</p>
          </div>
          <div>
            <Label className="text-muted-foreground text-xs">Member Since</Label>
            <p className="font-medium text-sm text-foreground">
              {company.created_at
                ? new Date(company.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
                : "—"}
            </p>
          </div>
        </div>

        {/* All fields read-only */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {([
            { key: "phone", label: "Phone", fallbackKey: "account_phone" },
            { key: "email", label: "Email", fallbackKey: "account_email" },
            { key: "website", label: "Website" },
            { key: "address_line1", label: "Address" },
            { key: "city", label: "City" },
            { key: "state", label: "State" },
            { key: "postal_code", label: "ZIP Code" },
          ] as const).map((entry) => {
            const { key, label } = entry;
            const fallbackKey = (entry as any).fallbackKey as string | undefined;
            const value =
              (company as any)[key] ||
              (fallbackKey ? (company as any)[fallbackKey] : null) ||
              "";
            if (!value) return null;
            return (
              <div key={key}>
                <Label className="text-muted-foreground text-xs">{label}</Label>
                <p className="font-medium text-sm text-foreground mt-1">{value}</p>
              </div>
            );
          })}
        </div>

        <p className="text-xs text-muted-foreground mt-6 pt-4 border-t border-border">
          Company identity details are protected for fraud and compliance purposes. To request a change, contact{" "}
          <a href="mailto:support@lanerunner.com" className="text-primary underline">support@lanerunner.com</a>.
        </p>
      </Card>
    </div>
  );
}
